import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { docsCanonicalPath, PUSH_TROUBLESHOOTING_SLUG } from '@gitcenter/shared';
import { groupApi, repoApi } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import './GroupPage.css';

interface Group {
    id: string;
    name: string;
    path: string;
    description?: string;
    visibility: string;
    parentId?: string;
    createdAt: string;
}

interface Member {
    id: string;
    userId: string;
    role: string;
    createdAt: string;
    user: {
        id: string;
        username: string;
        email: string;
        avatarUrl?: string;
    };
}

interface Repository {
    id: string;
    name: string;
    fullPath: string;
    description?: string;
    isPrivate: boolean;
    defaultBranch: string;
    updatedAt: string;
}

export function GroupPage() {
    const params = useParams();
    // 组合路径：:topLevel + (可选的) *
    const splat = params['*'];
    const path = params.topLevel
        ? (splat ? `${params.topLevel}/${splat}` : params.topLevel)
        : (splat || params.groupPath); // Fallback for safety
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();

    const [group, setGroup] = useState<Group | null>(null);
    const [subgroups, setSubgroups] = useState<Group[]>([]);
    const [projects, setProjects] = useState<Repository[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'overview' | 'projects' | 'members'>('overview');
    const [showNewRepoModal, setShowNewRepoModal] = useState(false);
    const [showNewSubgroupModal, setShowNewSubgroupModal] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (path) {
            loadGroup();
        } else {
            setLoading(false);
            setError('群组路径无效');
        }
    }, [path]);

    const loadGroup = async () => {
        try {
            setLoading(true);
            setError(null);

            // 使用聚合接口一次拉取群组 + 子群组 + 项目 + 成员（减少请求与延迟）
            const data = await groupApi.getWithInclude(path!);
            setGroup(data.group);
            setSubgroups(data.subgroups ?? []);
            setProjects(data.projects ?? []);
            setMembers(data.members ?? []);
        } catch (err: any) {
            setError(err.message || '加载群组失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRepo = async (data: { name: string; slug: string; description: string; visibility: string }) => {
        try {
            const repo = await repoApi.createInGroup(path!, {
                name: data.slug || data.name,
                description: data.description,
                isPrivate: data.visibility === 'private',
            });
            setShowNewRepoModal(false);
            navigate(`/${repo.fullPath}`);
        } catch (err: any) {
            setError(err.message || '创建仓库失败');
        }
    };

    const handleCreateSubgroup = async (data: { name: string; slug: string; description: string; visibility: string }) => {
        try {
            const newGroup = await groupApi.create({
                name: data.name,
                path: data.slug,
                description: data.description,
                visibility: data.visibility,
                parentId: group!.id,
            });
            setShowNewSubgroupModal(false);
            // 刷新子群组列表
            const subgroupsData = await groupApi.getSubgroups(path!);
            setSubgroups(subgroupsData);
            // 或者直接跳转到新群组页面? 
            // navigate(`/groups/${newGroup.path}`); // 既然是在当前群组内创建，也许留在这里比较好，或者看需求。
            // 这里选择刷新列表并提示成功（如果有提示机制的话），或者直接跳转。
            // 简单起见，跳转到新群组。
            navigate(`/groups/${newGroup.path}`);
        } catch (err: any) {
            setError(err.message || '创建子群组失败');
        }
    };

    // 删除群组
    const handleDeleteGroup = async () => {
        if (!group || !path) return;

        const confirmed = window.confirm(
            `确定要删除群组 "${group.name}" 吗？\n\n此操作不可恢复！`
        );

        if (!confirmed) return;

        try {
            setDeleting(true);
            await groupApi.delete(path);
            navigate('/groups');
        } catch (err: any) {
            alert(err.message || '删除群组失败');
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return (
            <div className="group-page">
                <div className="loading">加载中...</div>
            </div>
        );
    }

    if (error || !group) {
        return (
            <div className="group-page">
                <div className="error-state">
                    <h2>无法访问该群组</h2>
                    <p>{error || '群组不存在或您没有访问权限'}</p>
                    <Link to="/groups" className="btn btn-primary">
                        返回群组列表
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="group-page">
            {/* 群组头部 */}
            <div className="group-header">
                <div className="group-avatar">
                    {group.name.charAt(0).toUpperCase()}
                </div>
                <div className="group-title">
                    <h1>{group.name}</h1>
                    <span className="group-path">@{group.path}</span>
                    {group.description && (
                        <p className="group-description">{group.description}</p>
                    )}
                </div>
                <div className="group-actions">
                    <span className={`visibility-badge ${group.visibility}`}>
                        {group.visibility === 'public' ? '公开' : group.visibility === 'internal' ? '内部' : '私有'}
                    </span>
                    {isAuthenticated && (
                        <>
                            <button className="btn btn-secondary" onClick={() => setShowNewSubgroupModal(true)}>
                                新建子群组
                            </button>
                            <button className="btn btn-primary" onClick={() => setShowNewRepoModal(true)}>
                                新建项目
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* 标签页 */}
            <div className="tabs">
                <button
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    概览
                </button>
                <button
                    className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
                    onClick={() => setActiveTab('projects')}
                >
                    项目 ({projects.length})
                </button>
                <button
                    className={`tab ${activeTab === 'members' ? 'active' : ''}`}
                    onClick={() => setActiveTab('members')}
                >
                    成员 ({members.length})
                </button>
            </div>

            {/* 内容区 */}
            <div className="tab-content">
                {activeTab === 'overview' && (
                    <div className="overview-grid">
                        {/* 子群组 */}
                        {subgroups.length > 0 && (
                            <section className="section">
                                <h2>子群组</h2>
                                <div className="item-list">
                                    {subgroups.map((subgroup) => (
                                        <Link key={subgroup.id} to={`/groups/${subgroup.path}`} className="item-card">
                                            <div className="item-avatar group">
                                                {subgroup.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="item-info">
                                                <h3>{subgroup.name}</h3>
                                                <span className="item-path">@{subgroup.path}</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* 项目列表 */}
                        <section className="section">
                            <h2>项目</h2>
                            {projects.length === 0 ? (
                                <div className="empty-section">
                                    <p>该群组还没有项目</p>
                                    {isAuthenticated && (
                                        <button className="btn btn-secondary" onClick={() => setShowNewRepoModal(true)}>
                                            创建第一个项目
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="item-list">
                                    {projects.slice(0, 5).map((project) => (
                                        <Link key={project.id} to={`/${project.fullPath}`} className="item-card" data-repo-path={project.fullPath}>
                                            <div className="item-avatar repo">
                                                <svg viewBox="0 0 16 16" width="20" height="20">
                                                    <path fill="currentColor" d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Zm10.5-1h-8a1 1 0 0 0-1 1v6.708A2.486 2.486 0 0 1 4.5 9h8ZM5 12.25a.25.25 0 0 1 .25-.25h3.5a.25.25 0 0 1 .25.25v3.25a.25.25 0 0 1-.4.2l-1.45-1.087a.249.249 0 0 0-.3 0L5.4 15.7a.25.25 0 0 1-.4-.2Z" />
                                                </svg>
                                            </div>
                                            <div className="item-info">
                                                <h3>{project.name}</h3>
                                                {project.description && (
                                                    <p className="item-description">{project.description}</p>
                                                )}
                                            </div>
                                            {project.isPrivate && (
                                                <span className="private-badge">私有</span>
                                            )}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* 删除群组（仅当没有项目时显示） */}
                        {isAuthenticated && projects.length === 0 && (
                            <section className="section danger-zone">
                                <h2>危险区域</h2>
                                <div className="danger-item">
                                    <div className="danger-info">
                                        <strong>删除群组</strong>
                                        <p>删除后无法恢复，请谨慎操作</p>
                                    </div>
                                    <button
                                        className="btn btn-danger"
                                        onClick={handleDeleteGroup}
                                        disabled={deleting}
                                    >
                                        {deleting ? '删除中...' : '删除群组'}
                                    </button>
                                </div>
                            </section>
                        )}
                    </div>
                )}

                {activeTab === 'projects' && (
                    <div className="projects-tab">
                        <div className="tab-header">
                            <input
                                type="text"
                                placeholder="搜索项目..."
                                className="search-input"
                            />
                            {isAuthenticated && (
                                <button className="btn btn-primary" onClick={() => setShowNewRepoModal(true)}>
                                    新建项目
                                </button>
                            )}
                        </div>
                        {projects.length === 0 ? (
                            <div className="empty-section large">
                                <h3>还没有项目</h3>
                                <p>在这个群组中创建您的第一个项目</p>
                            </div>
                        ) : (
                            <div className="project-list">
                                {projects.map((project) => (
                                    <Link key={project.id} to={`/${project.fullPath}`} className="project-row" data-repo-path={project.fullPath}>
                                        <div className="project-icon">
                                            <svg viewBox="0 0 16 16" width="16" height="16">
                                                <path fill="currentColor" d="M2 2.5A2.5 2.5 0 0 1 4.5 0h8.75a.75.75 0 0 1 .75.75v12.5a.75.75 0 0 1-.75.75h-2.5a.75.75 0 0 1 0-1.5h1.75v-2h-8a1 1 0 0 0-.714 1.7.75.75 0 1 1-1.072 1.05A2.495 2.495 0 0 1 2 11.5Z" />
                                            </svg>
                                        </div>
                                        <div className="project-info">
                                            <span className="project-name">{project.name}</span>
                                            <span className="project-path">{project.fullPath}</span>
                                        </div>
                                        <div className="project-meta">
                                            {project.isPrivate && <span className="private-badge">私有</span>}
                                            <span className="branch-badge">{project.defaultBranch}</span>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'members' && (
                    <div className="members-tab">
                        <div className="tab-header">
                            <span className="member-count">{members.length} 位成员</span>
                        </div>
                        {members.length === 0 ? (
                            <div className="empty-section large">
                                <h3>还没有成员</h3>
                                <p>邀请团队成员加入这个群组</p>
                            </div>
                        ) : (
                            <div className="member-list">
                                {members.map((member) => (
                                    <div key={member.id} className="member-row">
                                        <div className="member-avatar">
                                            {member.user.username.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="member-info">
                                            <span className="member-name">{member.user.username}</span>
                                            <span className="member-role">{getRoleName(member.role)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* 新建仓库模态框 */}
            {showNewRepoModal && (
                <NewRepoModal
                    groupPath={path!}
                    onClose={() => setShowNewRepoModal(false)}
                    onSubmit={handleCreateRepo}
                />
            )}

            {/* 新建子群组模态框 */}
            {showNewSubgroupModal && (
                <NewSubgroupModal
                    parentPath={path!}
                    onClose={() => setShowNewSubgroupModal(false)}
                    onSubmit={handleCreateSubgroup}
                />
            )}
        </div>
    );
}

function getRoleName(role: string): string {
    const roleNames: Record<string, string> = {
        owner: '所有者',
        maintainer: '维护者',
        developer: '开发者',
        reporter: '报告者',
        guest: '访客',
    };
    return roleNames[role] || role;
}

function NewRepoModal({
    groupPath,
    onClose,
    onSubmit,
}: {
    groupPath: string;
    onClose: () => void;
    onSubmit: (data: { name: string; slug: string; description: string; visibility: string }) => void;
}) {
    // ... NewRepoModal implementation (reusing existing logic for slug generation)
    // 为了复用，这里可以提取一个通用的 ModalForm，但为了快速实现，我先复制逻辑修改。
    // 实际 NewRepoModal 和 NewSubgroupModal 极其相似。

    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [slugEdited, setSlugEdited] = useState(false);
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState('private');
    const [submitting, setSubmitting] = useState(false);

    // 自动生成 slug
    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleNameChange = (value: string) => {
        setName(value);
        if (!slugEdited) {
            setSlug(generateSlug(value));
        }
    };

    const handleSlugChange = (value: string) => {
        setSlugEdited(true);
        setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({ name, slug: slug || generateSlug(name), description, visibility });
        setSubmitting(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>创建空白项目</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <p className="form-description">
                            创建一个空白项目来存储您的文件、计划工作并协作开发。
                        </p>
                        <p className="form-description" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                            若首次 <code>git push</code> 失败（大仓库常见），请参阅
                            <Link to={`${docsCanonicalPath(PUSH_TROUBLESHOOTING_SLUG)}#silent-after-total`}> 推送排障文档</Link>。
                        </p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="project-name">项目名称</label>
                        <input
                            id="project-name"
                            type="text"
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="My project"
                            required
                        />
                        <span className="form-hint">
                            可以使用字母、数字、点号、短横线和空格
                        </span>
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>项目 URL</label>
                            <div className="input-with-prefix">
                                <span className="input-prefix">gitcenter.com/{groupPath}/</span>
                            </div>
                        </div>
                        <div className="form-group flex-1">
                            <label htmlFor="project-slug">项目标识 (slug)</label>
                            <input
                                id="project-slug"
                                type="text"
                                value={slug}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                placeholder="my-project"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="project-description">项目描述（可选）</label>
                        <textarea
                            id="project-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="简要描述这个项目的用途"
                            rows={2}
                        />
                    </div>

                    <div className="form-group">
                        <label>可见性级别</label>
                        <div className="visibility-cards">
                            <label className={`visibility-card ${visibility === 'private' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={visibility === 'private'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M4 4a4 4 0 0 1 8 0v2h.25c.966 0 1.75.784 1.75 1.75v5.5A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25v-5.5C2 6.784 2.784 6 3.75 6H4Zm8.25 3.5h-8.5a.25.25 0 0 0-.25.25v5.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-5.5a.25.25 0 0 0-.25-.25ZM10.5 6V4a2.5 2.5 0 1 0-5 0v2Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>私有</strong>
                                    <span>项目访问必须明确授予每个用户。如果此项目属于群组，则授予群组成员访问权限。</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'internal' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="internal"
                                    checked={visibility === 'internal'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M2 5.5a3.5 3.5 0 1 1 5.898 2.549 5.508 5.508 0 0 1 3.034 4.084.75.75 0 1 1-1.482.235 4 4 0 0 0-7.9 0 .75.75 0 0 1-1.482-.236A5.507 5.507 0 0 1 3.102 8.05 3.493 3.493 0 0 1 2 5.5Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>内部</strong>
                                    <span>任何已登录用户都可以访问此项目</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'public' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={visibility === 'public'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>公开</strong>
                                    <span>无需任何身份验证即可访问此项目</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            取消
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!name || !slug || submitting}
                        >
                            {submitting ? '创建中...' : '创建项目'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function NewSubgroupModal({
    parentPath,
    onClose,
    onSubmit,
}: {
    parentPath: string;
    onClose: () => void;
    onSubmit: (data: { name: string; slug: string; description: string; visibility: string }) => void;
}) {
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [slugEdited, setSlugEdited] = useState(false);
    const [description, setDescription] = useState('');
    const [visibility, setVisibility] = useState('private');
    const [submitting, setSubmitting] = useState(false);

    // 自动生成 slug
    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleNameChange = (value: string) => {
        setName(value);
        if (!slugEdited) {
            setSlug(generateSlug(value));
        }
    };

    const handleSlugChange = (value: string) => {
        setSlugEdited(true);
        setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        await onSubmit({ name, slug: slug || generateSlug(name), description, visibility });
        setSubmitting(false);
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>创建子群组</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="form-section">
                        <p className="form-description">
                            群组允许您管理项目和成员，类似于文件夹。
                        </p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="group-name">群组名称</label>
                        <input
                            id="group-name"
                            type="text"
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder="My Subgroup"
                            required
                        />
                        <span className="form-hint">
                            可以使用字母、数字、点号、短横线和空格
                        </span>
                    </div>

                    <div className="form-row">
                        <div className="form-group flex-1">
                            <label>群组 URL</label>
                            <div className="input-with-prefix">
                                <span className="input-prefix">gitcenter.com/groups/{parentPath}/</span>
                            </div>
                        </div>
                        <div className="form-group flex-1">
                            <label htmlFor="group-slug">群组标识 (slug)</label>
                            <input
                                id="group-slug"
                                type="text"
                                value={slug}
                                onChange={(e) => handleSlugChange(e.target.value)}
                                placeholder="my-subgroup"
                                required
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="group-description">群组描述（可选）</label>
                        <textarea
                            id="group-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="简要描述这个群组的用途"
                            rows={2}
                        />
                    </div>

                    <div className="form-group">
                        <label>可见性级别</label>
                        <div className="visibility-cards">
                            <label className={`visibility-card ${visibility === 'private' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="private"
                                    checked={visibility === 'private'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M4 4a4 4 0 0 1 8 0v2h.25c.966 0 1.75.784 1.75 1.75v5.5A1.75 1.75 0 0 1 12.25 15h-8.5A1.75 1.75 0 0 1 2 13.25v-5.5C2 6.784 2.784 6 3.75 6H4Zm8.25 3.5h-8.5a.25.25 0 0 0-.25.25v5.5c0 .138.112.25.25.25h8.5a.25.25 0 0 0 .25-.25v-5.5a.25.25 0 0 0-.25-.25ZM10.5 6V4a2.5 2.5 0 1 0-5 0v2Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>私有</strong>
                                    <span>该群组及其项目只能由成员访问。</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'internal' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="internal"
                                    checked={visibility === 'internal'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M2 5.5a3.5 3.5 0 1 1 5.898 2.549 5.508 5.508 0 0 1 3.034 4.084.75.75 0 1 1-1.482.235 4 4 0 0 0-7.9 0 .75.75 0 0 1-1.482-.236A5.507 5.507 0 0 1 3.102 8.05 3.493 3.493 0 0 1 2 5.5Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>内部</strong>
                                    <span>任何已登录用户都可以访问此群组。</span>
                                </div>
                            </label>

                            <label className={`visibility-card ${visibility === 'public' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="visibility"
                                    value="public"
                                    checked={visibility === 'public'}
                                    onChange={(e) => setVisibility(e.target.value)}
                                />
                                <div className="visibility-icon">
                                    <svg viewBox="0 0 16 16" width="20" height="20">
                                        <path fill="currentColor" d="M8 0a8 8 0 1 1 0 16A8 8 0 0 1 8 0ZM1.5 8a6.5 6.5 0 1 0 13 0 6.5 6.5 0 0 0-13 0Z" />
                                    </svg>
                                </div>
                                <div className="visibility-info">
                                    <strong>公开</strong>
                                    <span>无需任何身份验证即可访问此群组。</span>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            取消
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={!name || !slug || submitting}
                        >
                            {submitting ? '创建中...' : '创建群组'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default GroupPage;

